import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  BookOpenIcon,
  UserIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  DocumentIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  AwardIcon,
  PaperClipIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import CourseContentViewer from './CourseContentViewer';
import CertificateAssignment from './CertificateAssignment';
import EnrollmentModal from './EnrollmentModal';
import toast from 'react-hot-toast';

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [courseProgress, setCourseProgress] = useState(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  useEffect(() => {
    fetchCourseDetails();
    if (user?.role === 'student') {
      checkEnrollmentStatus();
      fetchCourseProgress();
    }
  }, [id, user]);

  const fetchCourseProgress = async () => {
    try {
      const response = await axios.get(`/api/course-progress/${id}`);
      setCourseProgress(response.data);
    } catch (error) {
      console.error('Error fetching course progress:', error);
    }
  };

  const fetchCourseDetails = async () => {
    try {
      const response = await axios.get(`/api/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Course not found');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const checkEnrollmentStatus = async () => {
    try {
      const response = await axios.get(`/api/enrollments/student/${user._id}`);
      const enrolled = response.data.some(enrollment =>
        enrollment.course._id === id && enrollment.status === 'enrolled'
      );
      setIsEnrolled(enrolled);
    } catch (error) {
      console.error('Error checking enrollment:', error);
    }
  };

  const handleEnrollClick = () => {
    setShowEnrollmentModal(true);
  };

  const handleEnroll = async (useWallet = false) => {
    try {
      setEnrollmentLoading(true);

      // If course fee is zero, use existing server enroll endpoint
      if (!course.fees || course.fees === 0) {
        const response = await axios.post('/api/enrollments', { courseId: id });
        setIsEnrolled(true);
        toast.success('Enrolled successfully');
        fetchCourseDetails();
        return;
      }

      // Create Razorpay order on the server with wallet option
      const orderRes = await axios.post('/api/enrollments/create-order', {
        courseId: id,
        useWallet: useWallet
      });
      if (!orderRes.data || !orderRes.data.orderId) {
        // Backend might return a helpful message when Razorpay isn't configured
        const msg = orderRes.data?.message || 'Unable to create payment order';
        toast.error(msg);
        return;
      }

      const { orderId, amount, currency, key, walletAmountUsed, finalAmount } = orderRes.data;

      // If wallet covers the full amount, no payment needed
      if (amount === 0) {
        setIsEnrolled(true);
        toast.success('Enrolled successfully using wallet!');
        fetchCourseDetails();
        return;
      }

      // Load Razorpay script if not already loaded
      const loadRazorpay = () => new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(window.Razorpay);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(window.Razorpay);
        script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
        document.body.appendChild(script);
      });

      await loadRazorpay();

      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: course.title,
        description: `Enroll: ${course.courseCode} (₹${walletAmountUsed > 0 ? walletAmountUsed + ' from wallet + ' + finalAmount + ' to pay' : course.fees})`,
        order_id: orderId,
        handler: async function (response) {
          // Client-side verification (optional) - server webhook is authoritative
          try {
            const verifyRes = await axios.post('/api/enrollments/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              courseId: id
            });

            setIsEnrolled(true);
            toast.success('Payment successful & enrolled!');
            fetchCourseDetails();
            // Navigate to a simple payment success page with details
            try {
              navigate(`/payment-success?courseId=${id}&paymentId=${response.razorpay_payment_id}`);
            } catch (navErr) {
              /* ignore navigation errors */
            }
          } catch (err) {
            // Verification failed - inform user but webhook may still complete enrollment
            console.error('Payment verify error:', err);
            const msg = err.response?.data?.message || 'Payment processed, verification failed. Enrollment will be confirmed shortly.';
            toast.error(msg);
          }
        },
        modal: {
          ondismiss: function () {
            // User closed the payment modal without completing payment
            toast('Payment cancelled. You can try again anytime.');
          }
        },
        prefill: {
          name: user?.firstName + ' ' + user?.lastName,
          email: user?.email
        },
        notes: {
          courseId: id,
          studentId: user?._id
        },
        theme: {
          color: '#2563eb'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Enrollment error:', error); // Debug log
      const message = error.response?.data?.message || 'Failed to initiate enrollment';
      toast.error(message);
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handleUnenroll = async () => {
    try {
      // Find enrollment ID and delete
      const enrollments = await axios.get(`/api/enrollments/student/${user._id}`);
      const enrollment = enrollments.data.find(e => e.course._id === id);

      if (enrollment) {
        await axios.delete(`/api/enrollments/${enrollment._id}`);
        setIsEnrolled(false);
        toast.success('Successfully unenrolled from course');
        fetchCourseDetails();
      }
    } catch (error) {
      toast.error('Failed to unenroll from course');
    }
  };

  const handleApproveCourse = async () => {
    try {
      await axios.put(`/api/courses/${id}/approve`);
      toast.success('Course approved successfully!');
      fetchCourseDetails(); // Refresh course data
    } catch (error) {
      toast.error('Failed to approve course');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Course not found</h2>
      </div>
    );
  }

  const canEdit = (user?.role === 'instructor' && course.instructor._id === user._id) || user?.role === 'admin';
  const canApprove = user?.role === 'admin'; // Separate permission for approval

  return (
    <div className="">
      {/* Course Header */}
      <div className="card mb-3">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4 mb-4">
              <div className="h-16 w-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <BookOpenIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
                <p className="text-lg text-gray-600">{course.courseCode}</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">{course.description}</p>

            {/* Course Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              {user?.role !== 'student' && (
                <div className="flex items-center text-gray-600">
                  <UserIcon className="h-5 w-5 mr-3" />
                  <span>Instructor: {course.instructor.firstName} {course.instructor.lastName}</span>
                </div>
              )}

              <div className="flex items-center text-gray-600">
                <CalendarIcon className="h-5 w-5 mr-3" />
                {/* <span>{course.credits} Credits • {course.level}</span> */}
                <span>{course.level}</span>
              </div>

              <div className="flex items-center text-gray-600">
                <ClockIcon className="h-5 w-5 mr-3" />
                <span>Duration: {formatDuration(course.totalDuration || 0)}</span>
              </div>

              <div className="flex items-center text-gray-600">
                <CurrencyRupeeIcon className="h-5 w-5 mr-3" />
                <span>{course.fees}</span>
              </div>
              {user?.role !== 'student' && (
                <div className="flex items-center text-gray-600">
                  <ClockIcon className="h-5 w-5 mr-3" />
                  <span>{course.currentEnrollment}/{course.maxStudents} enrolled</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 lg:mt-0 lg:ml-6 flex flex-col space-y-3">
            {canEdit && (
              <>
                <button
                  onClick={() => navigate(`/edit-course/${id}`)}
                  className="btn btn-secondary flex items-center justify-center"
                >
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Edit Course
                </button>
                <button
                  onClick={() => navigate(`/courses/${id}/materials`)}
                  className="btn btn-primary flex items-center justify-center"
                >
                  <DocumentIcon className="h-5 w-5 mr-2" />
                  Manage Materials
                </button>
              </>
            )}

            {canApprove && !course.isApproved && (
              <button
                onClick={handleApproveCourse}
                className="btn btn-primary flex items-center justify-center"
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Approve Course
              </button>
            )}

            {/* {canApprove && (
              <button
                className="btn btn-danger flex items-center justify-center"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                Deactivate Course
              </button>
            )} */}

            {user?.role === 'student' && (
              !isEnrolled && (
                <button
                  onClick={handleEnrollClick}
                  disabled={enrollmentLoading || course.currentEnrollment >= course.maxStudents || !course.isApproved}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {enrollmentLoading ? 'Enrolling...' :
                    !course.isApproved ? 'Pending Approval' :
                      course.currentEnrollment >= course.maxStudents ? 'Course Full' : 'Enroll Now'}
                </button>
              )
            )}
          </div>
        </div>
        {/* Certificate Download Information */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-0 mt-3">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="text-xs leading-relaxed">
                To download your course certificate: Complete all course materials, submit the certificate assignment,
                and achieve the minimum passing score. Once approved by the instructor, the download option will be available
                in the certificate section below.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Materials - Now visible to all users */}
      <CourseContentViewer
        courseId={id}
        materials={course.materials || []}
        isEnrolled={isEnrolled}
        canEdit={canEdit}
        onEnroll={handleEnrollClick}
        enrollmentLoading={enrollmentLoading}
      />

      {/* Prerequisites */}
      {course.prerequisites?.length > 0 && (
        <div className="card mt-3">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Prerequisites</h2>
          <ul className="list-disc list-inside space-y-1">
            {course.prerequisites.map((prereq, index) => (
              <li key={index} className="text-gray-700">{prereq}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollmentModal && (
        <EnrollmentModal
          course={course}
          user={user}
          onEnroll={handleEnroll}
          onClose={() => setShowEnrollmentModal(false)}
          enrollmentLoading={enrollmentLoading}
        />
      )}

      {/* Certificate Assignment - Only show for enrolled students or instructors/admins */}
      {isEnrolled || user?.role !== 'student' ? (
        <CertificateAssignment courseId={id} course={course} isEnrolled={isEnrolled} />
      ) : null}
    </div>
  );
};

export default CourseDetail;
